import { InputLabel, HelperText, SegmentedButtons } from '@scale/scaleui';
import { Text } from '@scale/scaleui-radix';
import React, { useState, useMemo } from 'react';
import MarkdownToolbar from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/toolbar/MarkdownToolbar';
import MarkdownEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/MarkdownEditorContext';
import PlainTextEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/PlainTextEditorContext';
import {
  Editor,
  EditorContainer,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor';
import { useMarkdownSyntaxOptions } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMarkdownSyntaxOptions';

enum EditorType {
  Markdown = 'markdown',
  PlainText = 'plaintext',
}

const StringInputBuilder: React.FC<{
  name: string;
  updateProperty: (value: any) => void;
  existingValue?: string;
  required?: boolean;
  hint?: string;
  error?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  useMarkdownEditor?: boolean;
}> = ({
  name,
  updateProperty,
  existingValue,
  required,
  hint,
  error,
  readOnly,
  placeholder,
  useMarkdownEditor = false,
}) => {
  const markdownSyntaxConfig = useMarkdownSyntaxOptions();
  const [value, setValue] = useState<string>(existingValue || '');

  const [editorType, setEditorType] = useState<EditorType>(EditorType.Markdown);

  const warning = useMemo(() => {
    if (name === 'Field ID' && value !== value.trim()) {
      return 'Field ID contains leading or trailing whitespaces.';
    }
    return undefined;
  }, [name, value]);

  const markdownEditorComponent = (
    <MarkdownEditorContext defaultTokens={markdownSyntaxConfig} initialContent={value}>
      <EditorContainer>
        <MarkdownToolbar />
        <Editor
          content={value}
          placeholder={placeholder}
          onContentChange={(content: string) => {
            const normalizedText = content.replaceAll('\\_', '_');
            setValue(normalizedText);
          }}
          onEditorBlur={({ content }) => {
            const normalizedText = content.replaceAll('\\_', '_');
            if (!required && normalizedText === '') {
              updateProperty(undefined);
            } else {
              updateProperty(normalizedText);
            }
          }}
          readOnly={readOnly}
        />
      </EditorContainer>
    </MarkdownEditorContext>
  );

  const plainTextEditorComponent = (
    <>
      <PlainTextEditorContext initialContent={value}>
        <EditorContainer className="w-full">
          <Editor
            content={value}
            placeholder={placeholder}
            onContentChange={(content: string) => {
              setValue(content);
            }}
            onEditorBlur={({ content }) => {
              if (!required && content === '') {
                updateProperty(undefined);
              } else {
                updateProperty(content);
              }
            }}
            disabled={readOnly}
          />
        </EditorContainer>
      </PlainTextEditorContext>
      {warning && (
        <Text className="px-1 w-full pt-1" color="warning" size="1">
          {warning}
        </Text>
      )}
      <HelperText className="px-1 pt-1 w-full">{hint}</HelperText>
    </>
  );

  return (
    <div className="flex flex-col items-center scaleui scale-theme scale-tailwind">
      <div className="flex items-start w-full">
        <InputLabel required={required} error={error} disabled={readOnly}>
          {name}
        </InputLabel>
      </div>
      {useMarkdownEditor ? (
        <div className="w-full">
          <SegmentedButtons
            buttons={[
              { id: EditorType.Markdown, label: 'Markdown' },
              { id: EditorType.PlainText, label: 'Plain Text' },
            ]}
            selected={editorType}
            onSelect={id => setEditorType(id as EditorType)}
          />
        </div>
      ) : null}
      {useMarkdownEditor && editorType === EditorType.Markdown ? (
        <div className="pt-2 w-full items-start">{markdownEditorComponent}</div>
      ) : (
        plainTextEditorComponent
      )}
    </div>
  );
};

export default StringInputBuilder;
